/**
 * One media slot: a photograph, or a video where the editor gave one.
 *
 * `shared.media` carries both an `image` and a `videoUrl`, and every section that draws
 * a media frame should go through here rather than reaching for `media.image` itself -
 * otherwise a video an editor enters silently renders as its poster and nothing plays.
 *
 * The image doubles as the video's poster, so filling both in is the good case, not a
 * contradiction: the frame has something to show before anyone presses play.
 */
import { Image, SIZES } from './Image';
import { VideoEmbed } from './VideoEmbed';
import type { MediaValue } from '../../types/sections';

/** True when there is anything at all to draw - use it to decide whether to render the figure. */
export function hasMedia(media?: MediaValue | null): boolean {
  return Boolean(media && (media.image || media.videoUrl));
}

/**
 * `shared.media.aspect` as a Tailwind class, or `null` for "the slot's usual shape".
 *
 * The field has existed since the model was generated and almost nothing read it, so a
 * frame drew whatever proportion its section had hard-coded. That is why the same
 * `split-content-media` is 672x401 on the home page (`3883:70418`) and 672x620 on
 * Narzedzia online (`3883:71200`): the file changes the photograph's shape and the
 * section had no way to hear about it.
 *
 * **`rectangle` and `auto` deliberately return nothing.** The proportion a slot falls
 * back to is not one number across the site - a hero band measures 1360x760 and a split
 * row 672x401 - so each section passes its own measured default and only `square` and
 * `wide` override it. Mapping `rectangle` to a single ratio here would have quietly
 * reshaped every band and row already checked against the file.
 */
export function aspectClass(media?: MediaValue | null): string | null {
  switch (media?.aspect) {
    case 'square':   return 'aspect-square';
    case 'photo':    return 'aspect-[4/3]';
    case 'wide':     return 'aspect-[16/9]';
    case 'panorama': return 'aspect-[21/9]';
    default:         return null;
  }
}

export function MediaFrame({
  media, sizes = SIZES.half, className = 'h-full w-full object-cover', title
}: {
  media?: MediaValue | null;
  sizes?: string;
  className?: string;
  /** Names the player for a screen reader when the slot holds a video. */
  title?: string;
}) {
  if (!media) return null;

  // `decorative` is the editor saying the picture carries no information, and it has to
  // win over both the component's alt and the file's: an empty alt is what makes a screen
  // reader skip a background photograph instead of announcing it. The publish guard makes
  // the choice unavoidable - a picture is described, or it is declared decoration.
  const alt = media.decorative ? '' : (media.alt ?? media.image?.alternativeText ?? '');

  if (media.videoUrl) {
    return <VideoEmbed url={media.videoUrl} poster={media.image} alt={alt} title={title} sizes={sizes} className={className} />;
  }

  return media.image ? <Image media={media.image} alt={alt} sizes={sizes} className={className} /> : null;
}
